/**
 * @fileoverview
 * This is an example file of how to hook up PoseNet data to a 2D renderer.
 */

/**
 * An exploration into new open-source facial-recognition tech which is
 *    supported by machine learning. We can use this technology to create
 *    highly-performant, complex imagery/animations which we hook up to 2D or
 *    3D graphics without too much hassle.
 */
class Engine {
  /** @return {Promise<Recorder|*>} */
  static async importRecorderModule() {
    const recorderModule = await import('./components/recorder');
    return recorderModule.default;
  }

  /** @return {Promise<PosenetWrapper|*>} */
  static async importPoseNetModule() {
    const posenetModule = await import('./components/posenetWrapper');
    return posenetModule.default;
  }

  /** @return {Promise<PoseObserver|*>} */
  static async importPoseObserver() {
    const poseObserverModule = await import('./components/posenetWrapper/poseObserver');
    return poseObserverModule.default;
  }

  /**
   * @constructs {Experiment}
   * @param {HTMLElement} rootElement
   */
  constructor(rootElement) {
    /**
     * A utility class that aides with setting up the webcam and capturing a
     *    stream.
     * @type {?Recorder}
     * @private
     */
    this._recorder = null;

    /**
     * A heavily documented utility class aiming to clarify some of TensorFlow's
     *    usages.
     * @type {?PosenetWrapper}
     * @private
     */
    this._poseNet = null;

    /**
     * A utility class that links the Recorder with PoseNet and constantly
     *    requests updates.
     * @type {?PoseObserver}
     * @private
     */
    this._poseObserver = null;

    this._rootElement = rootElement;
  }

  /**
   * Initialise the app.
   * @return {Promise<void>|false}
   */
  async initialise() {
    const textNode = document.createElement('p');
    textNode.classList.add('anim');
    textNode.setAttribute('aria-hidden', '');
    this._rootElement.appendChild(textNode);

    textNode.innerText = 'Requesting webcam';
    const Recorder = await Engine.importRecorderModule();
    this._recorder = new Recorder({
      outputVideoConstraints: {
        minWidth: 1280,
        minHeight: 720,
      },
      outputAudioConstraints: false,
      width: 640,
      height: 360,
      flipOutput: true,
    });
    // Request a MediaStream from the users capture device.
    await this._recorder.enableStream();
    // Connect the MediaStream to the video element.
    this._recorder.connect();


    textNode.innerText = 'Downloading PoseNet';
    const PoseNetWrapper = await Engine.importPoseNetModule();
    // Initialise PoseNet
    this._poseNet = new PoseNetWrapper();
    textNode.innerText = 'Loading machine-learned data';
    // Request and load PoseNet's pre-trained machine learning data.
    await this._poseNet.loadModel();


    textNode.innerText = 'Linking webcam to PoseNet';
    const PoseObserver = await Engine.importPoseObserver();
    // Initialise the PoseObserver, a utility class that links our MediaStream
    //    with PoseNet and constantly requests updates.
    this._poseObserver = new PoseObserver(this._recorder, this._poseNet);
    // Start an agnostic rendering loop purely to update data from PoseNet.
    this._poseObserver.start();

    textNode.innerText = 'ヽ(o＾▽＾o)ノ';
    textNode.classList.add('hide');

    return true;
  }

  /**
   * TODO: Dispose.
   */
  dispose() {}

  /**
   * Useful for if you want to play with the data in a different environment.
   * @return {Array<Pose>|Pose}
   */
  get pose() {
    return this._poseObserver.lastPose;
  }

  /** @return {Recorder} */
  get recorder() {
    return this._recorder;
  }
}

export default Engine;
